/**
 * Pass list + parameter model for Manual welding Status Configuration.
 *
 * Manual welding has no weld parameter file, so the pass list is defined by the
 * user instead of being derived from machine limits data.
 */

export const MANUAL_PASS_BASE_NAMES = ['Root', 'Hot', 'Fill', 'Cap', 'Strip', 'Weave'] as const;

export type ManualPassBaseName = (typeof MANUAL_PASS_BASE_NAMES)[number];

export type ManualPassCounts = Record<ManualPassBaseName, number>;

/** Oscillation Width and True Energy are not applicable to Manual welding. */
export const MANUAL_STATUS_PARAMETERS = [
  { key: 'current', label: 'Current', unit: 'A' },
  { key: 'volts', label: 'Volts', unit: 'V' },
  { key: 'wire_speed', label: 'Wire Speed', unit: 'in/min' },
  { key: 'travel_speed', label: 'Travel Speed', unit: 'in/min' },
  { key: 'heat', label: 'Heat Input', unit: 'kJ/in' },
] as const;

export type ManualStatusParameterKey = (typeof MANUAL_STATUS_PARAMETERS)[number]['key'];

export interface ManualPassLimit {
  min: string;
  max: string;
}

export interface ManualPass {
  id: string;
  name: string;
  limits: Record<string, ManualPassLimit>;
}

export const MAX_PASSES_PER_BASE_NAME = 20;

export function createEmptyPassCounts(): ManualPassCounts {
  return MANUAL_PASS_BASE_NAMES.reduce((counts, name) => {
    counts[name] = 0;
    return counts;
  }, {} as ManualPassCounts);
}

export function createEmptyPassLimits(): Record<string, ManualPassLimit> {
  return MANUAL_STATUS_PARAMETERS.reduce((limits, parameter) => {
    limits[parameter.key] = { min: '', max: '' };
    return limits;
  }, {} as Record<string, ManualPassLimit>);
}

export function manualParameterLabel(parameter: { label: string; unit: string }): string {
  return `${parameter.label} (${parameter.unit})`;
}

/**
 * Count 0 omits the pass, count 1 uses the bare base name, and higher counts
 * append the pass number ("Fill 1", "Fill 2", …).
 */
export function generateManualPassNames(counts: Partial<ManualPassCounts>): string[] {
  return MANUAL_PASS_BASE_NAMES.flatMap((baseName) => {
    const count = normalizePassCount(counts[baseName]);
    if (count === 0) return [];
    if (count === 1) return [baseName];
    return Array.from({ length: count }, (_, index) => `${baseName} ${index + 1}`);
  });
}

/** Rebuilds pass counts from canonical names such as "Root" or "Root 1". */
export function getManualPassCounts(passes: Pick<ManualPass, 'name'>[]): ManualPassCounts {
  const counts = createEmptyPassCounts();
  passes.forEach((pass) => {
    const normalizedName = pass.name.trim().toLowerCase();
    const baseName = MANUAL_PASS_BASE_NAMES.find((candidate) => {
      const normalizedBase = candidate.toLowerCase();
      return (
        normalizedName === normalizedBase ||
        new RegExp(`^${normalizedBase}\\s+\\d+$`, 'i').test(normalizedName)
      );
    });
    if (baseName) counts[baseName] += 1;
  });
  return counts;
}

export function normalizePassCount(value: unknown): number {
  const parsed = Math.floor(Number(value));
  if (!Number.isFinite(parsed) || parsed <= 0) return 0;
  return Math.min(MAX_PASSES_PER_BASE_NAME, parsed);
}

let passIdSequence = 0;

export function createManualPassId(): string {
  passIdSequence += 1;
  return `manual-pass-${Date.now().toString(36)}-${passIdSequence}`;
}

export function createManualPass(name: string): ManualPass {
  return { id: createManualPassId(), name, limits: createEmptyPassLimits() };
}

/** Sort rank for display/save: Root → Hot → Fill → Cap → Strip → Weave (then numbered variants). */
export function getManualPassSortRank(name: string): number {
  const normalizedName = name.trim().toLowerCase();
  for (let index = 0; index < MANUAL_PASS_BASE_NAMES.length; index += 1) {
    const base = MANUAL_PASS_BASE_NAMES[index].toLowerCase();
    if (normalizedName === base) return index * 1000;
    const numbered = normalizedName.match(new RegExp(`^${base}\\s+(\\d+)$`));
    if (numbered) return index * 1000 + Number(numbered[1]);
  }
  return MANUAL_PASS_BASE_NAMES.length * 1000;
}

/** Always display passes in canonical base-name order. */
export function sortManualPasses<T extends Pick<ManualPass, 'name'>>(passes: T[]): T[] {
  return [...passes].sort((a, b) => {
    const rankDiff = getManualPassSortRank(a.name) - getManualPassSortRank(b.name);
    if (rankDiff !== 0) return rankDiff;
    return a.name.localeCompare(b.name, undefined, { sensitivity: 'base', numeric: true });
  });
}

/** Keeps already entered limits when a pass of the same name survives regeneration. */
export function buildManualPasses(names: string[], existingPasses: ManualPass[] = []): ManualPass[] {
  const remaining = [...existingPasses];
  const built = names.map((name) => {
    const matchIndex = remaining.findIndex(
      (pass) => pass.name.trim().toLowerCase() === name.trim().toLowerCase()
    );
    if (matchIndex === -1) return createManualPass(name);
    const [matched] = remaining.splice(matchIndex, 1);
    return { ...matched, name };
  });
  return sortManualPasses(built);
}

export function nextManualPassName(existingNames: string[]): string {
  const taken = new Set(existingNames.map((name) => name.trim().toLowerCase()));
  for (let index = 1; index <= 100; index += 1) {
    const candidate = `Pass ${index}`;
    if (!taken.has(candidate.toLowerCase())) return candidate;
  }
  return `Pass ${existingNames.length + 1}`;
}

/** Base pass types currently configured on a job (for edit-pass dialog). */
export function getSelectedPassBaseNames(passes: Pick<ManualPass, 'name'>[]): ManualPassBaseName[] {
  const counts = getManualPassCounts(passes);
  return MANUAL_PASS_BASE_NAMES.filter((name) => counts[name] > 0);
}

export function findDuplicatePassNames(passes: ManualPass[]): string[] {
  const seen = new Set<string>();
  const duplicates = new Set<string>();
  passes.forEach((pass) => {
    const key = pass.name.trim().toLowerCase();
    if (!key) return;
    if (seen.has(key)) duplicates.add(pass.name.trim());
    seen.add(key);
  });
  return Array.from(duplicates);
}
